import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { formatIncompletePhoneNumber } from 'libphonenumber-js';
import { Avatar, CircularProgress, Grid } from '@mui/material';

import Modal from '../../shared/Modal';
import { Radio } from '../../shared/RadioGroup';
import { ReactComponent as PlusIcon } from '../../../../assets/icons/Plus.svg';

import { useStylesDuplicateWorkerModal } from './styles';
import { WorkerInvitationStatus } from 'models/general';

interface IWorker {
  id: string;
  firstName: string;
  lastName: string;
  badgeCodes: string[];
  userContacts: {
    contactType: number;
    contactValue: string;
    isPrimary: boolean;
  }[];
  pictureUrl?: string;
  dateOfBirth?: string;
  invitationStatus?: WorkerInvitationStatus;
}

interface IDuplicateWorkerModalProps {
  duplicateWorkers: IWorker[];
  onClose: () => void;
  onProceedWithSelectedWorker: (worker: IWorker) => void;
  onAddNewWorker: () => void;
  isWorker?: boolean;
  hasMore?: boolean;
  onLoadMore?: () => void;
  isLoadingMore?: boolean;
}

const DuplicateWorkerModal = ({
  duplicateWorkers,
  onClose,
  onProceedWithSelectedWorker,
  onAddNewWorker,
  isWorker,
  hasMore,
  onLoadMore,
  isLoadingMore,
}: IDuplicateWorkerModalProps) => {
  const { t } = useTranslation();
  const classes = useStylesDuplicateWorkerModal();
  const [selectedWorker, setSelectedWorker] = useState<IWorker | null>(
    duplicateWorkers?.length === 1 ? duplicateWorkers[0] : null,
  );
  const onEndReached = useRef(false);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.currentTarget;
    const threshold = 50; // pixels from bottom
    const isNearBottom = target.scrollHeight - target.scrollTop - target.clientHeight < threshold;

    if (isNearBottom && !onEndReached.current) {
      onEndReached.current = true;
      setTimeout(() => {
        onEndReached.current = false; // reset for next time
      }, 1000);
      if (hasMore && onLoadMore && !isLoadingMore) {
        onLoadMore();
      }
    }
  };

  return (
    <Modal
      title={t('basicInformation.duplicateWorkerModal.title')}
      modalStyleClass={classes.modal}
      modalContentStyleClass={classes.modalContentStyleClass}
      modalActionsStyleClass={classes.modalActionsStyleClass}
      Content={
        <>
          <div onScroll={handleScroll} className={classes.scrollContainer}>
            {duplicateWorkers?.map(worker => {
              const primaryEmail = worker.userContacts?.find(c => c.contactType === 0 && c.isPrimary)?.contactValue;
              const secondaryEmails =
                worker.userContacts?.filter(c => c.contactType === 0 && !c.isPrimary).map(c => c.contactValue) || [];
              const primaryPhone = worker.userContacts?.find(c => c.contactType === 1 && c.isPrimary)?.contactValue;
              const secondaryPhones =
                worker.userContacts?.filter(c => c.contactType === 1 && !c.isPrimary).map(c => c.contactValue) || [];
              const badgeNumber = worker.badgeCodes?.length > 0 ? worker.badgeCodes.join(', ') : null;
              const formattedDob = worker.dateOfBirth ? moment(worker.dateOfBirth).format('MMM DD, YYYY') : null;
              const disabled =
                isWorker &&
                ((!primaryEmail && !primaryPhone) || worker?.invitationStatus === WorkerInvitationStatus.NOT_INVITED);
              return (
                <div
                  key={worker.id}
                  className={`${classes.workerItem} ${selectedWorker?.id === worker.id ? classes.selectedWorkerItem : ''} ${disabled ? classes.disabledWorkerItem : ''}`}
                  onClick={() => !disabled && setSelectedWorker(worker)}
                >
                  <div className={classes.workerHeader}>
                    <Avatar
                      alt={`${worker.firstName?.toUpperCase() || ''} ${worker.lastName?.toUpperCase() || ''}`}
                      className={classes.avatar}
                      src={worker.pictureUrl || ''}
                    />
                    <div className={classes.workerInfo}>
                      <p className={classes.name}>
                        {worker.firstName} {worker.lastName}
                      </p>
                    </div>
                  </div>
                  <Grid container className={classes.fieldsContainer}>
                    {badgeNumber && (
                      <Grid item xs={12} sm={6}>
                        <p className={classes.fieldLabel}>{t('basicInformation.duplicateWorkerModal.badgeNumber')}</p>
                        <p className={classes.fieldValue}>{badgeNumber}</p>
                      </Grid>
                    )}
                    {formattedDob && (
                      <Grid item xs={12} sm={6}>
                        <p className={classes.fieldLabel}>{t('basicInformation.duplicateWorkerModal.dateOfBirth')}</p>
                        <p className={classes.fieldValue}>{formattedDob}</p>
                      </Grid>
                    )}
                    {primaryEmail && (
                      <Grid item xs={12}>
                        <p className={classes.fieldLabel}>{t('basicInformation.duplicateWorkerModal.primaryEmail')}</p>
                        <p className={classes.fieldValue}>{primaryEmail}</p>
                      </Grid>
                    )}
                    {secondaryEmails.length > 0 && (
                      <Grid item xs={12}>
                        <p className={classes.fieldLabel}>
                          {t('basicInformation.duplicateWorkerModal.secondaryEmail')}
                        </p>
                        {secondaryEmails.map((email, idx) => (
                          <p key={idx} className={classes.fieldValue}>
                            {email}
                          </p>
                        ))}
                      </Grid>
                    )}
                    {primaryPhone && (
                      <Grid item xs={12}>
                        <p className={classes.fieldLabel}>{t('basicInformation.duplicateWorkerModal.primaryPhone')}</p>
                        <p className={classes.fieldValue}>{formatIncompletePhoneNumber(primaryPhone)}</p>
                      </Grid>
                    )}
                    {secondaryPhones.length > 0 && (
                      <Grid item xs={12}>
                        <p className={classes.fieldLabel}>
                          {t('basicInformation.duplicateWorkerModal.secondaryPhone')}
                        </p>
                        {secondaryPhones.map((phone, idx) => (
                          <p key={idx} className={classes.fieldValue}>
                            {formatIncompletePhoneNumber(phone)}
                          </p>
                        ))}
                      </Grid>
                    )}
                  </Grid>
                  <Radio
                    checked={selectedWorker?.id === worker.id}
                    disabled={disabled}
                    onChange={() => setSelectedWorker(worker)}
                    className={classes.radio}
                  />
                </div>
              );
            })}
            {isLoadingMore && (
              <div className={classes.loadingMore}>
                <CircularProgress size={24} />
              </div>
            )}
          </div>
        </>
      }
      actionButtons={[
        {
          label: (
            <div className={classes.addNewWorkerLink}>
              <PlusIcon className={classes.plusIcon} />
              {t('basicInformation.duplicateWorkerModal.addNewWorker')}
            </div>
          ),
          variant: 'text',
          testId: 'duplicate-worker-add-new-button',
          onClick: onAddNewWorker,
        },
        {
          label: t('basicInformation.duplicateWorkerModal.proceedWithSelected'),
          disabled: !selectedWorker?.id,
          testId: 'duplicate-worker-proceed-button',
          onClick: () => onProceedWithSelectedWorker(selectedWorker),
        },
      ]}
      onClose={onClose}
    />
  );
};

export default React.memo(DuplicateWorkerModal);
